import { ScanEventChange } from './change-types';

/**
 * Utility functions for processing scan event changes
 */

/**
 * Group changes by type for easier processing
 */
export const groupChangesByType = (
	changes: ScanEventChange[]
): Record<string, ScanEventChange[]> => {
	return changes.reduce(
		(acc, change) => {
			if (!acc[change.type]) {
				acc[change.type] = [];
			}
			acc[change.type].push(change);
			return acc;
		},
		{} as Record<string, ScanEventChange[]>
	);
};

/**
 * Get all scan additions
 */
export const getScanAdditions = (changes: ScanEventChange[]) => {
	return changes.filter(
		(change): change is Extract<ScanEventChange, { type: 'ADD_SCAN' }> => change.type === 'ADD_SCAN'
	);
};

/**
 * Get all scan removals
 */
export const getScanRemovals = (changes: ScanEventChange[]) => {
	return changes.filter(
		(change): change is Extract<ScanEventChange, { type: 'REMOVE_SCAN' }> =>
			change.type === 'REMOVE_SCAN'
	);
};

/**
 * Get all photo additions
 */
export const getPhotoAdditions = (changes: ScanEventChange[]) => {
	return changes.filter(
		(change): change is Extract<ScanEventChange, { type: 'ADD_LOAD_PHOTO' | 'ADD_DOCKET_PHOTO' }> =>
			change.type === 'ADD_LOAD_PHOTO' || change.type === 'ADD_DOCKET_PHOTO'
	);
};

/**
 * Get all field updates
 */
export const getFieldUpdates = (changes: ScanEventChange[]) => {
	return changes.filter(
		(
			change
		): change is Extract<
			ScanEventChange,
			{
				type:
					| 'UPDATE_DOCKET_ID'
					| 'UPDATE_WEIGHT'
					| 'UPDATE_TRUCK_REGISTRATION'
					| 'UPDATE_CONTAMINATION_AMOUNT'
					| 'UPDATE_NOTES';
			}
		> =>
			change.type === 'UPDATE_DOCKET_ID' ||
			change.type === 'UPDATE_WEIGHT' ||
			change.type === 'UPDATE_TRUCK_REGISTRATION' ||
			change.type === 'UPDATE_CONTAMINATION_AMOUNT' ||
			change.type === 'UPDATE_NOTES'
	);
};

/**
 * Check if there are any changes to submit
 */
export const hasChanges = (changes: ScanEventChange[]): boolean => {
	return changes.length > 0;
};

/**
 * Get a summary of changes for display/logging
 */
export const getChangesSummary = (changes: ScanEventChange[]): string => {
	const grouped = groupChangesByType(changes);
	const summary: string[] = [];

	Object.entries(grouped).forEach(([type, typeChanges]) => {
		summary.push(`${type}: ${typeChanges.length}`);
	});

	return summary.join(', ') || 'No changes';
};
